import { db } from '../utils/db';

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes default TTL

/**
 * Cache an API response in IndexedDB.
 * @param {string} url - The request URL (used as cache key)
 * @param {Object} data - The response data to cache
 * @param {number} ttl - Time-to-live in ms (default 5 min)
 */
export async function cacheResponse(url, data, ttl = CACHE_TTL) {
  try {
    const key = normalizeUrl(url);
    const existing = await db.apiCache.get(key);
    if (existing) {
      await db.apiCache.update(key, {
        data: JSON.stringify(data),
        timestamp: Date.now(),
        ttl
      });
    } else {
      await db.apiCache.put({
        url: key,
        data: JSON.stringify(data),
        timestamp: Date.now(),
        ttl
      });
    }
  } catch (err) {
    console.warn('[apiCache] Failed to cache response:', err);
  }
}

/**
 * Retrieve a cached API response.
 * Returns null if not cached or expired.
 * @param {string} url - The request URL
 * @param {boolean} ignoreExpiry - If true, return even expired cache (for offline)
 * @returns {Object|null} The cached data, or null
 */
export async function getCachedResponse(url, ignoreExpiry = false) {
  try {
    const key = normalizeUrl(url);
    const cached = await db.apiCache.get(key);
    if (!cached) return null;

    const isExpired = Date.now() - cached.timestamp > cached.ttl;
    if (isExpired && !ignoreExpiry) return null;

    return JSON.parse(cached.data);
  } catch (err) {
    console.warn('[apiCache] Failed to read cache:', err);
    return null;
  }
}

/**
 * Clear all cached responses.
 */
export async function clearCache() {
  try {
    await db.apiCache.clear();
  } catch (err) {
    console.warn('[apiCache] Failed to clear cache:', err);
  }
}

/**
 * Clear cached GET responses for asset-management API paths.
 */
export async function clearAssetManagementApiCache() {
  try {
    await db.apiCache.filter((item) => String(item.url || '').includes('asset-management')).delete();
  } catch (err) {
    console.warn('[apiCache] Failed to clear asset-management cache:', err);
  }
}

/**
 * Clear expired cache entries.
 */
export async function clearExpiredCache() {
  try {
    const now = Date.now();
    await db.apiCache.filter(item => now - item.timestamp > item.ttl).delete();
  } catch (err) {
    console.warn('[apiCache] Failed to clear expired cache:', err);
  }
}

/**
 * Normalize URL for consistent cache keys (strip trailing slashes, sort params).
 */
function normalizeUrl(url) {
  return url.replace(/\/+$/, '').toLowerCase();
}
